import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, FileText, User, Settings, RefreshCw, Lock, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import PasswordChange from './PasswordChange';

const Navbar = () => {
  const { user, logout, refreshUserData } = useAuth();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const userMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePasswordChangeSuccess = () => {
    setShowPasswordChange(false);
    setShowUserMenu(false);
  };

  const handlePasswordChangeCancel = () => {
    setShowPasswordChange(false);
  };

  if (!user || location.pathname === '/login') {
    return null;
  }

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-200 no-select">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link 
                to="/dashboard" 
                className="flex items-center space-x-2 text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors"
              >
                <FileText className="h-6 w-6 text-primary-600" />
                <span>Wellum PDF Viewer</span>
              </Link>
              
              {/* Navigation Links */}
              <nav className="flex items-center space-x-4">
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/dashboard'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  <span>Dokumentumok</span>
                </Link>
                
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      location.pathname === '/admin'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    <span>Adminisztráció</span>
                  </Link>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* User Menu Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded-md px-3 py-2 hover:bg-gray-50"
                >
                  <User className="h-4 w-4" />
                  <span>Üdvözöljük, <strong>{user.username}</strong></span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {user.role === 'admin' ? 'Adminisztrátor' : 'Ügyfél'}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                        Bejelentkezve mint: {user.username}
                        {user.role === 'admin' && (
                          <div className="text-xs text-gray-400">
                            Role: {user.role}
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => {
                          setShowPasswordChange(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <Lock className="h-4 w-4" />
                        <span>Jelszó megváltoztatása</span>
                      </button>
                      
                      <button
                        onClick={refreshUserData}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        <span>Adatok frissítése</span>
                      </button>
                      
                      <div className="border-t border-gray-100">
                        <button
                          onClick={logout}
                          className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center space-x-2"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Kijelentkezés</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md">
            <PasswordChange 
              onSuccess={handlePasswordChangeSuccess}
              onCancel={handlePasswordChangeCancel}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar; 